'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { CaretLeftIcon, CaretRightIcon } from '@phosphor-icons/react';
import { cn } from '@/lib/utils';

/**
 * A horizontal chip/tile rail. On touch devices it's a plain swipeable
 * overflow row; on fine-pointer devices (desktop — where there's nothing to
 * swipe with) quiet arrow buttons appear over faded edges whenever the rail
 * actually overflows in that direction. The arrows scroll by ~80% of the
 * visible width and respect prefers-reduced-motion.
 */
export function ScrollRail({
  className = '',
  railClassName = '',
  children,
}: {
  /** Wrapper classes (positioning context for the arrows). */
  className?: string;
  /** Classes for the scrolling row itself — gap/padding live here. */
  railClassName?: string;
  children: React.ReactNode;
}) {
  const railRef = useRef<HTMLDivElement>(null);
  const [finePointer, setFinePointer] = useState(false);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  useEffect(() => {
    const query = window.matchMedia('(pointer: fine)');
    setFinePointer(query.matches);
    const onChange = (e: MediaQueryListEvent) => setFinePointer(e.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  const updateEdges = useCallback(() => {
    const rail = railRef.current;
    if (!rail) return;
    // 1px slack: fractional scroll widths report scrollLeft just short of the max.
    setCanLeft(rail.scrollLeft > 1);
    setCanRight(rail.scrollLeft + rail.clientWidth < rail.scrollWidth - 1);
  }, []);

  useEffect(() => {
    const rail = railRef.current;
    if (!rail) return;
    updateEdges();
    rail.addEventListener('scroll', updateEdges, { passive: true });
    const observer = new ResizeObserver(updateEdges);
    observer.observe(rail);
    for (const child of rail.children) observer.observe(child);
    return () => {
      rail.removeEventListener('scroll', updateEdges);
      observer.disconnect();
    };
  }, [updateEdges, children]);

  const scrollBy = (direction: 1 | -1) => {
    const rail = railRef.current;
    if (!rail) return;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    rail.scrollBy({
      left: direction * Math.round(rail.clientWidth * 0.8),
      behavior: reduced ? 'auto' : 'smooth',
    });
  };

  const showLeft = finePointer && canLeft;
  const showRight = finePointer && canRight;

  return (
    <div className={cn('relative min-w-0', className)}>
      <div ref={railRef} className={cn('scrollbar-hide flex overflow-x-auto', railClassName)}>
        {children}
      </div>

      {showLeft && (
        <span className="pointer-events-none absolute inset-y-0 left-0 flex w-12 items-center bg-linear-to-r from-surface via-surface/80 to-transparent">
          <button
            type="button"
            aria-label="Rolar para a esquerda"
            onClick={() => scrollBy(-1)}
            className="pointer-events-auto grid size-7 shrink-0 place-items-center rounded-full border border-border-subtle bg-surface text-text-secondary shadow-sm hover:bg-surface-sunken hover:text-text-primary"
          >
            <CaretLeftIcon className="size-3.5" weight="bold" />
          </button>
        </span>
      )}
      {showRight && (
        <span className="pointer-events-none absolute inset-y-0 right-0 flex w-12 items-center justify-end bg-linear-to-l from-surface via-surface/80 to-transparent">
          <button
            type="button"
            aria-label="Rolar para a direita"
            onClick={() => scrollBy(1)}
            className="pointer-events-auto grid size-7 shrink-0 place-items-center rounded-full border border-border-subtle bg-surface text-text-secondary shadow-sm hover:bg-surface-sunken hover:text-text-primary"
          >
            <CaretRightIcon className="size-3.5" weight="bold" />
          </button>
        </span>
      )}
    </div>
  );
}
